import React from 'react';
import { Phone, Star, ArrowRight } from 'lucide-react';
import { AGENT } from '@/data/agent';
import { LANDING } from '@/constants/testIds';

const Hero = () => {
  const scrollToForm = (e) => {
    e.preventDefault();
    const el = document.getElementById('enquire');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section
      data-testid={LANDING.hero}
      className="relative bg-white overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-12 pb-20 lg:pt-20 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Left content */}
        <div className="lg:col-span-7 animate-fade-up">
          <div className="flex items-center gap-3 mb-6">
            <div className="bayleys-divider" />
            <p className="text-[11px] sm:text-xs tracking-[0.3em] uppercase text-bayleys-navy/70 font-medium">
              Residential Sales — Hibiscus Coast
            </p>
          </div>

          <h1
            data-testid={LANDING.heroName}
            className="text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] font-bold text-bayleys-navy leading-[0.95] tracking-tight"
          >
            Kaid<br />Maycock
          </h1>

          <p
            data-testid={LANDING.heroTagline}
            className="mt-8 text-lg sm:text-xl text-bayleys-navy/80 max-w-xl leading-relaxed font-normal"
          >
            Your trusted local Bayleys agent. Over a decade on the coast,
            <span className="font-medium text-bayleys-navy"> {AGENT.careerVolume} in career sales</span>,
            and a 5-star rating from clients across the Hibiscus Coast.
          </p>

          {/* Rating badge */}
          <a
            data-testid={LANDING.heroRatingBadge}
            href={AGENT.reviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-3 group"
          >
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className="w-4 h-4 fill-bayleys-gold text-bayleys-gold"
                />
              ))}
            </div>
            <span className="text-sm text-bayleys-navy/80 group-hover:text-bayleys-navy transition-colors">
              <span className="font-medium">{AGENT.rating.toFixed(1)}</span>
              <span className="mx-2 text-bayleys-navy/30">•</span>
              <span>{AGENT.reviewCount} verified reviews</span>
            </span>
          </a>

          {/* CTAs */}
          <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <a
              data-testid={LANDING.heroAppraisalBtn}
              href="#enquire"
              onClick={scrollToForm}
              className="group inline-flex items-center justify-center gap-2 bg-bayleys-navy hover:bg-bayleys-navy-hover text-white px-8 py-4 text-sm font-medium tracking-wider uppercase transition-all duration-200 hover:translate-x-0.5"
            >
              Request an appraisal
              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
            </a>
            <a
              data-testid={LANDING.heroCallBtn}
              href={AGENT.phoneHref}
              className="inline-flex items-center justify-center gap-2 border border-bayleys-navy text-bayleys-navy hover:bg-bayleys-navy hover:text-white px-8 py-4 text-sm font-medium tracking-wider uppercase transition-all duration-200"
            >
              <Phone className="w-4 h-4" />
              Call Kaid
            </a>
          </div>
        </div>

        {/* Right photo */}
        <div className="lg:col-span-5 relative">
          <div className="relative aspect-[4/5] w-full max-w-md mx-auto lg:max-w-none">
            {/* Navy backdrop block */}
            <div className="absolute -bottom-6 -right-6 w-3/4 h-3/4 bg-bayleys-navy hidden md:block" />
            {/* Gold thin accent */}
            <div className="absolute -top-3 -left-3 w-24 h-px bg-bayleys-gold hidden md:block" />
            <div className="absolute -top-3 -left-3 w-px h-24 bg-bayleys-gold hidden md:block" />
            <img
              data-testid={LANDING.heroPhoto}
              src={AGENT.photo}
              alt={`${AGENT.fullName}, Bayleys Real Estate Agent`}
              className="relative w-full h-full object-cover shadow-xl"
              loading="eager"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
